import * as React from 'react';

import { Utilities } from '../../../MedicareProAPI/Utilities/Utilities';
import { Form } from 'react-bootstrap';
import dateFormat from 'dateformat';

export interface ConsentBFormProps {
  onChange: ({}) => void;
  formData: any;
  onPolicyToggle: ({}) => void;
}
/**
 * Component for filling out a Consent Form B
 *
 * @author Andrew Brown <abrown@medicareprocrm.com>
 */
export const ConsentBForm = (props: ConsentBFormProps): React.JSX.Element => {
  const [showPlanInformation, setShowPlanInformation] = React.useState<boolean>(true);
  const date = new Date();
  const thisYear = date.getFullYear();
  const nextYear = thisYear + 1;
  const reviewDatesOptions = [thisYear, nextYear];

  const handleIncludePlanData = (value) => {
    setShowPlanInformation(value);
    props.onPolicyToggle(value);
  };

  return (
    <>
      <div className="row gutter-2">
        <div className="col-12 p-2 font-weight-bold border-top border-info mt-3 mb-2 boldHeaderLabel">
          Consumer Information
        </div>

        <div className="col-12 col-lg-7 mb-2">
          <label className="label">Consumer Name</label>
          <input
            type="text"
            className="form-control form-control-sm keep"
            name="soaBeneficiaryName"
            value={props.formData.soaBeneficiaryName}
            onChange={props.onChange}
            required
          />
          {!props.formData.soaBeneficiaryName && (
            <small className="text-danger">Field is required</small>
          )}
        </div>
        <div className="col-12 col-lg-5 mb-2">
          <label className="label">Consumer Phone</label>
          <input
            type="text"
            className="form-control form-control-sm keep"
            name="soaBeneficiaryPhone"
            value={props.formData.soaBeneficiaryPhone}
            onChange={props.onChange}
            required
          />
          {!props.formData.soaBeneficiaryPhone && (
            <small className="text-danger">Field is required</small>
          )}
        </div>
        <div className="col-12  mb-2">
          <label className="label">Consumer Address</label>
          <input
            type="text"
            className="form-control form-control-sm keep"
            name="soaBeneficiaryAddress"
            value={props.formData.soaBeneficiaryAddress}
            onChange={props.onChange}
            required
          />
          {!props.formData.soaBeneficiaryAddress && (
            <small className="text-danger">Field is required</small>
          )}
        </div>
      </div>
      <div className="row gutter-2">
        <div className="col-12 p-2 font-weight-bold border-top border-info mt-3 mb-2 boldHeaderLabel">
          Income Information
        </div>
        {props.formData.soaIncome === 0 && (
          <div className="col-12">
            <div className="alert alert-info p-1" style={{ fontSize: '9pt' }}>
              <i className="far fa-info-circle mr-1"></i>Income information can load from a saved
              income data. Add to the private info section first for better record keeping.
            </div>
          </div>
        )}
        <div className="col-12 col-lg-4 mb-2">
          <label className="label">Household Income</label>
          <input
            type="text"
            className="form-control form-control-sm keep"
            name="soaIncome"
            required
            value={props.formData.soaIncome}
            onChange={props.onChange}
          />
        </div>
        <div className="col-12 col-lg-4 mb-2">
          <label className="label">Income Year</label>
          <select
            className="form-control form-control-sm"
            name="soaIncomeYear"
            required
            onChange={props.onChange}
          >
            {reviewDatesOptions.map((option) => {
              return (
                <option selected={props.formData.soaIncomeYear == option ? true : false}>
                  {option}
                </option>
              );
            })}
          </select>
        </div>
        <div className="col-12 col-lg-4 mb-2">
          <label className="label">Application Review Date</label>
          <input
            type="date"
            className="keep form-control form-control-sm needsSlashes"
            name="soaReviewDate"
            value={props.formData.soaReviewDate}
            onChange={props.onChange}
            required
          />
        </div>
      </div>
      <div className="row gutter-2">
        <div className="col-12 p-2 font-weight-bold border-top border-info mt-3 mb-2 boldHeaderLabel">
          Plan Information
          {showPlanInformation && (
            <span
              className="badge badge-warning ml-2 cp"
              onClick={() => handleIncludePlanData(false)}
            >
              <i className="far fa-times-circle mr-1"></i>Remove from document
            </span>
          )}
          {!showPlanInformation && (
            <span className="badge badge-info ml-2 cp" onClick={() => handleIncludePlanData(true)}>
              Include in document
            </span>
          )}
        </div>
        {props.formData.consentCompany == '' && (
          <div className="col-12">
            <div className="alert alert-info p-1" style={{ fontSize: '9pt' }}>
              <i className="far fa-info-circle mr-1"></i>Plan information can load from a saved
              policy. Add the policy first for better record keeping. These are optional fields
            </div>
          </div>
        )}

        {showPlanInformation && (
          <>
            <div className="col-12 col-lg-4 mb-2">
              <label className="label">Insurance Company</label>
              <input
                type="text"
                className="form-control form-control-sm keep"
                name="consentCompany"
                required
                value={props.formData.consentCompany}
                onChange={props.onChange}
              />
            </div>
            <div className="col-12 col-lg-8 mb-2">
              <label className="label">Insurance Plan</label>
              <input
                type="text"
                className="form-control form-control-sm keep"
                name="consentPlan"
                required
                value={props.formData.consentPlan}
                onChange={props.onChange}
              />
            </div>
            <div className="col-12 col-lg-4 mb-2">
              <label className="label">Policy Effective</label>
              <input
                type="date"
                className="keep form-control form-control-sm needsSlashes"
                name="consentEffective"
                value={props.formData.consentEffective}
                onChange={props.onChange}
                required
              />
            </div>
            <div className="col-12 col-lg-4 mb-2">
              <label className="label">Applied Subsidy</label>
              <input
                type="text"
                className="form-control form-control-sm keep"
                name="consentSubsidy"
                required
                value={props.formData.consentSubsidy}
                onChange={props.onChange}
              />
            </div>
            <div className="col-12 col-lg-4 mb-2">
              <label className="label">Monthly Premium for Insured</label>
              <input
                type="text"
                className="form-control form-control-sm keep"
                name="consentPremium"
                required
                value={props.formData.consentPremium}
                onChange={props.onChange}
              />
            </div>
          </>
        )}
      </div>
      <div className="row gutter-2">
        <div className="col-12 p-2 font-weight-bold border-top border-info mt-3 mb-2 boldHeaderLabel">
          Agent Information
        </div>

        <div className="col-12 col-lg-4 mb-2">
          <label className="label">Agent Name</label>
          <input
            type="text"
            className="form-control form-control-sm keep"
            name="soaAgentName"
            value={props.formData.soaAgentName}
            onChange={props.onChange}
            required
          />
          {!props.formData.soaAgentName && <small className="text-danger">Field is required</small>}
        </div>
        <div className="col-12 col-lg-4 mb-2">
          <label className="label">Agent Phone</label>
          <input
            type="text"
            className="form-control form-control-sm keep"
            name="soaAgentPhone"
            value={props.formData.soaAgentPhone}
            onChange={props.onChange}
            required
          />
          {!props.formData.soaAgentPhone && (
            <small className="text-danger">Field is required</small>
          )}
        </div>
        <div className="col-12 col-lg-4 mb-2">
          <label className="label">Agent NPN</label>
          <input
            type="text"
            className="form-control form-control-sm keep"
            name="soaAgentNpn"
            value={props.formData.soaAgentNpn}
            onChange={props.onChange}
          />
        </div>
        <div className="col-12">
          <div className="alert alert-info p-1" style={{ fontSize: '9pt' }}>
            <i className="far fa-info-circle mr-1"></i>You will sign the document once its been
            completed by the beneficiary. Your dashboard will list documents that need your
            attention. Click{' '}
            <a href="https://www.medicareproapp.com/agent/settings/index/notifications">here</a> to
            configure notifications.
          </div>
        </div>
      </div>
    </>
  );
};
